import { KeyboardEvent, useCallback, useRef, useState } from 'react';

import { Paperclip, Send } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';

import useFileAttachments from '../lib/useFileAttachments';
import AttachmentList from './AttachmentList';

interface Props {
  onSend: (text: string, files: File[]) => void;
  disabled?: boolean;
  maxFiles?: number;
}

const MessageComposer = ({ onSend, disabled = false, maxFiles = 7 }: Props) => {
  const [localMsg, setLocalMsg] = useState('');
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const {
    attachments,
    onFileChange,
    onDragEnter,
    onDragOver,
    onDragLeave,
    onDrop,
    isDragActive,
    isInvalid,
    error,
    remove,
    clear,
  } = useFileAttachments(maxFiles);

  const handleSend = useCallback(() => {
    const text = localMsg.trim();
    const files = attachments.map((attachment) => attachment.file);
    if (!text && files.length === 0) return;
    onSend(text, files);
    setLocalMsg('');
    clear();
  }, [localMsg, attachments, onSend, clear]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend]
  );

  const handleAttachmentClick = () => {
    fileInputRef.current?.click();
  };

  const sendDisabled = disabled || (!localMsg.trim() && attachments.length === 0);

  return (
    <div
      className={cn(
        'shrink-0 border-t border-border-subtle bg-bg-surface shadow-[0_-1px_4px_rgba(0,0,0,0.04)]',
        isDragActive && (isInvalid ? 'bg-error-light' : 'bg-bg-surface-raised')
      )}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {error && (
        <Alert variant="destructive" className="mx-3 mt-3 rounded-lg border-border-subtle">
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <AttachmentList attachments={attachments} onRemove={remove} />
      <div className="flex items-end gap-3 p-4">
        {maxFiles > 0 && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={handleAttachmentClick}
            aria-label="Adjuntar archivo"
            disabled={disabled}
          >
            <Paperclip className="size-4" />
          </Button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          multiple
          onChange={onFileChange}
          className="hidden"
          accept="image/*,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
        />
        <Textarea
          value={localMsg}
          onChange={(e) => setLocalMsg(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Escribe un mensaje..."
          className="min-h-10 max-h-40 resize-none border-border-subtle text-base leading-5 text-text-default md:text-base"
          disabled={disabled}
        />
        <Button
          type="button"
          onClick={handleSend}
          disabled={sendDisabled}
          aria-label="Enviar mensaje"
          size="icon"
          className={cn(
            'rounded-lg bg-brand-vivid text-text-inverse hover:bg-brand-sky-dark hover:text-text-inverse',
            'disabled:pointer-events-none disabled:bg-brand-vivid disabled:text-text-inverse disabled:opacity-50'
          )}
        >
          <Send className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default MessageComposer;
